import { useTranslations } from '@vocab/react';
import { Text, TextLinkButton } from 'braid-design-system';
import React, { useMemo, type ReactNode } from 'react';
import { useDispatch } from 'react-redux';

import type { Origin } from 'src/components/Profile/Resume/types';

import { getResumeLink } from '../../../../actionCreators';

import translations from './.vocab';
import { ErrorMessageType } from './utils';

interface Props {
  id: string;
  origin: Origin;
  errorType: ErrorMessageType | '';
  onDelete: () => void;
  onSensitiveInfoClick: () => void;
}

export const ErrorMessage = ({
  id,
  origin,
  errorType,
  onDelete,
  onSensitiveInfoClick,
}: Props) => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();

  const errorLink = useMemo(() => {
    if (errorType === ErrorMessageType.Pending) {
      const onDownload = () => {
        dispatch(getResumeLink(id, origin));
      };
      return (
        <TextLinkButton onClick={onDownload}>{t('Try again')}</TextLinkButton>
      );
    }
    if (errorType === ErrorMessageType.Virus) {
      return (
        <TextLinkButton onClick={onDelete}>{t('Delete now')}</TextLinkButton>
      );
    }
  }, [dispatch, errorType, id, onDelete, origin, t]);

  const resumeErrors: Record<string, ReactNode | ReactNode[]> = useMemo(
    () => ({
      [ErrorMessageType.Delete]: t("Couldn't delete resumé"),
      [ErrorMessageType.Download]: t("Couldn't download resumé"),
      [ErrorMessageType.Default]: t("Couldn't make default"),
      [ErrorMessageType.Virus]: t("Virus detected, resumé can't be used."),
      [ErrorMessageType.Pending]: t(
        'Virus scanning, resumé not yet available.',
      ),
      [ErrorMessageType.SensitiveInformation]: t(
        'May contain sensitive information',
        {
          TextLinkButton: (v) => (
            <TextLinkButton onClick={onSensitiveInfoClick} weight="weak">
              {v}
            </TextLinkButton>
          ),
        },
      ),
    }),
    [onSensitiveInfoClick, t],
  );

  const getTextTone = () => {
    switch (errorType) {
      case ErrorMessageType.Pending:
        return 'info';
      case ErrorMessageType.SensitiveInformation:
        return 'caution';
      default:
        return 'critical';
    }
  };

  return Boolean(errorType) ? (
    <Text
      size="small"
      tone={getTextTone()}
      data={{ automation: `resume-error-${errorType.toLowerCase()}-${id}` }}
    >
      {resumeErrors[errorType]}
      &nbsp;{errorLink}
    </Text>
  ) : null;
};
